import { postWithAuth, fetchWithAuth } from "@/lib/api-helpers";
import type { Members } from "@/types/workspace";
import type { WorkspaceGroup, Workspace } from "@/types/workspace";
import { deleteWithAuth } from "@/lib/api-helpers";

export interface CreateGroupParams {
  workspaceId: string;
  name: string;
  members?: Members[];
}

export interface UpdateGroupParams {
  workspaceId: string;
  groupId: string;
  name?: string;
  members?: Members[];
}

export interface DeleteGroupParams {
  workspaceId: string;
  groupId: string;
}

export type GroupAccessPermission = "full" | "edit" | "view";

export interface GiveGroupAccessParams {
  workAreaId: string;
  groupId: string;
  permission: GroupAccessPermission;
}

export interface RemoveGroupAccessParams {
  workAreaId: string;
  groupId: string;
}

/**
 * Create a new group in a workspace
 */
export async function createGroup(params: CreateGroupParams): Promise<Workspace> {
  const response = await postWithAuth("/api/work-space/settings/group/create-group",{
      workspaceId: params.workspaceId,
      name: params.name,
      members: params.members || [],
    }
  );

  if ("isError" in response) {
    throw new Error(response.message || "Failed to create group");
  }

  return response.workspace;
}

/**
 * Update an existing group
 */
export async function updateGroup(params: UpdateGroupParams): Promise<Workspace> {
  const response = await postWithAuth<{ message: string; group: Workspace; workspace?: Workspace }>("/api/work-space/settings/group/update-group",{
      workspaceId: params.workspaceId,
      groupId: params.groupId,
      name: params.name,
      members: params.members,
    }
  );

  if ("isError" in response) {
    throw new Error(response.message || "Failed to update group");
  }

  // API returns {message, workspace} where workspace is the updated workspace
  return (response as any).workspace || (response as any).group || response;
}

/**
 * Delete a group
 */
export async function deleteGroup(params: DeleteGroupParams) {
    console.log("Deleting group", params);
  const response = await deleteWithAuth("/api/work-space/settings/group/delete-group", {
    body: JSON.stringify({
      workspaceId: params.workspaceId,
      groupId: params.groupId,
    }),
  });

  if ("isError" in (response as any)) {
    throw new Error((response as any).message || "Failed to delete group");
  }

  return (response as any).workspace;
}

/**
 * Grant a workspace group access to a WorkArea at the given permission level.
 * Backend dedupes by groupId — calling again with a new permission updates it.
 */
export async function giveGroupAccessToWorkArea(params: GiveGroupAccessParams) {
  const response = await postWithAuth<{ message: string; workArea: any }>(
    "/api/work-space/settings/group/give-group-access",
    {
      workAreaId: params.workAreaId,
      groupId: params.groupId,
      permission: params.permission,
    },
  );

  if ("isError" in (response as any)) {
    throw new Error((response as any).message || "Failed to give group access");
  }

  return (response as any).workArea;
}

/**
 * Revoke a workspace group's access to a WorkArea.
 */
export async function removeGroupAccessFromWorkArea(params: RemoveGroupAccessParams) {
  const response = await postWithAuth<{ message: string; workArea: any }>(
    "/api/work-space/settings/group/remove-group-access",
    {
      workAreaId: params.workAreaId,
      groupId: params.groupId,
    },
  );

  if ("isError" in (response as any)) {
    throw new Error((response as any).message || "Failed to remove group access");
  }

  return (response as any).workArea;
}

